import { useState } from 'react';
import type { ContactDto } from '@chat-emp/shared';

import { Button } from '../components/Button.js';
import { Icon } from '../components/Icon.js';
import { ApiRequestError } from '../lib/session.js';
import { useDismiss } from '../util/useDismiss.js';
import { startContactChat } from './api.js';
import { messageOf } from './useContacts.js';
import styles from './StartChatDialog.module.css';

interface StartChatDialogProps {
  contact: ContactDto;
  onStarted: (channelId: string) => void;
  onClose: () => void;
}

/** The first message to a contact, who receives it, and every reply after it, by email. */
export function StartChatDialog({ contact, onStarted, onClose }: StartChatDialogProps) {
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  /** `final` when the BFF says retrying cannot help, which holds Send for good. */
  const [failure, setFailure] = useState<{ text: string; final: boolean }>();

  // Once the message is on its way the chat exists; leaving would only hide that.
  const close = () => {
    if (!sending) onClose();
  };
  useDismiss(true, close);

  async function send() {
    setSending(true);
    setFailure(undefined);
    try {
      const { channelId } = await startContactChat({ contactId: contact.id, text: text.trim() });
      onStarted(channelId);
    } catch (err) {
      setFailure({
        text: messageOf(err, 'Could not start the chat.'),
        final: err instanceof ApiRequestError && !err.retryable,
      });
      setSending(false);
    }
  }

  const title = `Start a chat with ${contact.name}`;

  return (
    <div className={styles.overlay} onMouseDown={close}>
      <div
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className={styles.header}>
          <div className={styles.heading}>
            <h2 className={styles.title}>{title}</h2>
            <p className={styles.subtitle}>{contact.email}</p>
          </div>
          <button
            type="button"
            className={styles.close}
            onClick={close}
            disabled={sending}
            aria-label="Close"
          >
            <Icon name="close" size={20} />
          </button>
        </div>
        <div className={styles.body}>
          <textarea
            className={styles.textarea}
            aria-label="First message"
            placeholder="Write the first message. Replies arrive by email."
            value={text}
            onChange={(event) => setText(event.target.value)}
            maxLength={10000}
            autoFocus
          />
        </div>
        <div className={styles.footer}>
          {failure && (
            <p className={styles.error} role="alert">
              {failure.text}
            </p>
          )}
          <Button variant="ghost" onClick={close} disabled={sending}>
            Cancel
          </Button>
          <Button onClick={() => void send()} disabled={!text.trim() || sending || failure?.final}>
            Send
          </Button>
        </div>
      </div>
    </div>
  );
}
